import { useState } from 'react';
import { formatPrice, POSITIONS, SQUAD_RULES, XI_RULES, type Position, type SquadPlayer } from '@fantasy/types';
import { PlayerPicker, type PlayerPickerValue } from './PlayerPicker.tsx';

const POSITION_LABELS: Record<Position, string> = {
  GKP: 'Goalkeeper',
  DEF: 'Defender',
  MID: 'Midfielder',
  FWD: 'Forward',
};

type Slots = Record<Position, Array<SquadPlayer | null>>;

interface SquadMeta {
  name: string;
  gameweek: number;
  bank: number;
  freeTransfers: number;
}

interface SquadEditorProps {
  /** Pre-fills each position's slots. A slot with no player is an empty
   * PlayerPicker — this is what makes a partial import's recovery flow work:
   * matched players arrive pre-filled, unmatched slots arrive empty for the
   * user to search and fill themselves, instead of a placeholder CSV line. */
  initialPlayers: Partial<Record<Position, Array<SquadPlayer | null>>>;
  initialMeta: SquadMeta;
  onSave: (players: SquadPlayer[], meta: SquadMeta) => void;
  onCancel: () => void;
  saving: boolean;
}

/** Pads or trims a caller-supplied slot list to exactly the count a position
 * requires, so a partial import (or no import at all) always renders the
 * full fixed structure — missing slots are just empty pickers. */
function buildInitialSlots(initial: Partial<Record<Position, Array<SquadPlayer | null>>>): Slots {
  const slots = {} as Slots;
  for (const position of POSITIONS) {
    const count = SQUAD_RULES.byPosition[position];
    const given = initial[position] ?? [];
    slots[position] = Array.from({ length: count }, (_, i) => given[i] ?? null);
  }
  return slots;
}

function toPickerValue(player: SquadPlayer | null): PlayerPickerValue | null {
  if (!player || player.fplId === undefined) return null;
  return { fplId: player.fplId, name: player.name, position: player.position, club: player.club, price: player.price };
}

/** Applies `fn` to every filled slot across all four positions, leaving
 * empty slots untouched — the shared traversal behind the captain/vice
 * mutual-exclusivity rules below. */
function mapSlots(slots: Slots, fn: (player: SquadPlayer, position: Position, index: number) => SquadPlayer): Slots {
  const next = {} as Slots;
  for (const position of POSITIONS) {
    next[position] = slots[position].map((player, index) => (player ? fn(player, position, index) : player));
  }
  return next;
}

function computeProblems(filled: SquadPlayer[], bank: number): string[] {
  const problems: string[] = [];

  // The bank is allowed to show negative live, as a "you can't afford this"
  // signal while swapping players around — but it can't actually be saved
  // that way, since negative money isn't a real state. Caught here with a
  // specific message rather than letting the schema's min(0) reject it
  // silently after the request is already built.
  if (bank < 0) {
    problems.push(`The bank can't go negative (currently ${formatPrice(Math.round(bank * 10))}) — undo a swap or free up funds.`);
  }

  const startingCount = filled.filter((p) => p.isStarting).length;
  if (startingCount !== XI_RULES.size) {
    problems.push(`Exactly 11 players must be marked starting (currently ${startingCount}).`);
  }

  const captains = filled.filter((p) => p.isCaptain);
  if (captains.length === 0) problems.push('Select a captain.');
  if (captains.length > 1) problems.push('Only one player can be captain.');

  const vices = filled.filter((p) => p.isViceCaptain);
  if (vices.length === 0) problems.push('Select a vice-captain.');
  if (vices.length > 1) problems.push('Only one player can be vice-captain.');

  if (captains.length === 1 && vices.length === 1 && captains[0] === vices[0]) {
    problems.push('Captain and vice-captain must be different players.');
  }

  if (startingCount === XI_RULES.size) {
    const bench = filled.filter((p) => !p.isStarting);
    const orders = bench.map((p) => p.benchOrder);
    const isCompleteOrder = bench.length === 4 && new Set(orders).size === 4 && [1, 2, 3, 4].every((n) => orders.includes(n));
    if (!isCompleteOrder) {
      problems.push('Give each bench player a distinct order from 1 to 4.');
    }
  }

  return problems;
}

/**
 * Structured squad builder: a fixed slot for each of the 15 squad places,
 * each backed by a `PlayerPicker` so a slot can only ever hold a real player
 * from our ingested pool. Replaces the free-text CSV textarea, which is how
 * the Kinsky/Lammens wrong-club bug happened in the first place — a typo in
 * a club column had nothing stopping it from being taken as fact.
 */
export function SquadEditor({ initialPlayers, initialMeta, onSave, onCancel, saving }: SquadEditorProps) {
  const [slots, setSlots] = useState<Slots>(() => buildInitialSlots(initialPlayers));
  const [name, setName] = useState(initialMeta.name);
  const [gameweek, setGameweek] = useState(initialMeta.gameweek);
  const [bank, setBank] = useState(initialMeta.bank);
  const [freeTransfers, setFreeTransfers] = useState(initialMeta.freeTransfers);
  const [problems, setProblems] = useState<string[]>([]);

  // Recomputed on every render from whatever's currently in the slots — not
  // stored in state — so a replacement, or filling an empty slot, shows up
  // immediately rather than only after saving and re-loading the squad.
  const squadValue = POSITIONS.flatMap((position) => slots[position]).reduce(
    (total, player) => total + (player?.price ?? 0),
    0,
  );

  function updateSlot(position: Position, index: number, updater: (player: SquadPlayer) => SquadPlayer) {
    setSlots((prev) => {
      const next = { ...prev, [position]: [...prev[position]] };
      const current = next[position][index];
      if (current) next[position][index] = updater(current);
      return next;
    });
  }

  function onPickerChange(position: Position, index: number, value: PlayerPickerValue | null) {
    const previous = slots[position][index];

    setSlots((prev) => {
      const next = { ...prev, [position]: [...prev[position]] };
      next[position][index] = value
        ? {
            fplId: value.fplId,
            name: value.name,
            position: value.position as Position,
            club: value.club,
            price: value.price,
            // A replacement keeps everything about the slot it's filling —
            // starting/bench status, bench order, captain/vice-captain — only
            // the player identity changes. Otherwise swapping out a captain
            // silently drops the armband with no warning until save fails.
            isStarting: previous?.isStarting ?? false,
            benchOrder: previous && !previous.isStarting ? previous.benchOrder : undefined,
            isCaptain: previous?.isCaptain ?? false,
            isViceCaptain: previous?.isViceCaptain ?? false,
          }
        : null;
      return next;
    });

    // A swap moves the bank the way a real transfer does: selling the
    // previous occupant frees up its price, buying the new one spends it, so
    // the bank moves by the difference. Filling a previously-empty slot (a
    // screenshot import's unmatched slots, say) just spends, since there's
    // nothing sold to offset it. Deliberately allowed to go negative — that's
    // the "you can't actually afford this" signal, not something to clamp
    // away and hide.
    if (value) {
      const soldTenths = previous?.price ?? 0;
      // Rounded to the nearest tenth of a million — the unit money is
      // actually tracked in — rather than left as a raw float, which drifts
      // to values like 91.70000000000002 after enough swaps.
      setBank((prevBank) => Math.round((prevBank + (soldTenths - value.price) / 10) * 10) / 10);
    }
  }

  function toggleStarting(position: Position, index: number) {
    updateSlot(position, index, (player) => ({
      ...player,
      isStarting: !player.isStarting,
      benchOrder: player.isStarting ? player.benchOrder : undefined,
    }));
  }

  function toggleCaptain(position: Position, index: number) {
    setSlots((prev) =>
      mapSlots(prev, (player, pos, i) => {
        if (pos === position && i === index) {
          const willBeCaptain = !player.isCaptain;
          return { ...player, isCaptain: willBeCaptain, isViceCaptain: willBeCaptain ? false : player.isViceCaptain };
        }
        return player.isCaptain ? { ...player, isCaptain: false } : player;
      }),
    );
  }

  function toggleVice(position: Position, index: number) {
    setSlots((prev) =>
      mapSlots(prev, (player, pos, i) => {
        if (pos === position && i === index) {
          const willBeVice = !player.isViceCaptain;
          return { ...player, isViceCaptain: willBeVice, isCaptain: willBeVice ? false : player.isCaptain };
        }
        return player.isViceCaptain ? { ...player, isViceCaptain: false } : player;
      }),
    );
  }

  function setBenchOrder(position: Position, index: number, raw: string) {
    updateSlot(position, index, (player) => ({ ...player, benchOrder: raw === '' ? undefined : Number(raw) }));
  }

  function onSaveClick() {
    const ordered = POSITIONS.flatMap((position) => slots[position]);
    const missing = ordered.filter((p) => p === null).length;
    if (missing > 0) {
      setProblems([`Fill every slot before saving (${missing} empty).`]);
      return;
    }

    const filled = ordered as SquadPlayer[];
    const validationProblems = computeProblems(filled, bank);
    if (validationProblems.length > 0) {
      setProblems(validationProblems);
      return;
    }

    setProblems([]);
    onSave(filled, { name, gameweek, bank, freeTransfers });
  }

  return (
    <section className="panel rise-in" aria-label="Squad editor">
      <div className="row">
        <div className="field">
          <label htmlFor="squad-editor-name">Name</label>
          <input id="squad-editor-name" type="text" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="squad-editor-gameweek">Gameweek</label>
          <input
            id="squad-editor-gameweek"
            type="number"
            min={1}
            max={38}
            value={gameweek}
            onChange={(e) => setGameweek(Number(e.target.value))}
          />
        </div>
        <div className="field">
          <label htmlFor="squad-editor-bank">In the bank</label>
          <input
            id="squad-editor-bank"
            type="number"
            // No min: a swap can push this negative (see onPickerChange),
            // and the field needs to accept that value back, not reject it.
            step={0.1}
            value={bank}
            onChange={(e) => setBank(Number(e.target.value))}
          />
        </div>
        <div className="field">
          <label htmlFor="squad-editor-free-transfers">Free transfers</label>
          <input
            id="squad-editor-free-transfers"
            type="number"
            min={0}
            max={5}
            value={freeTransfers}
            onChange={(e) => setFreeTransfers(Number(e.target.value))}
          />
        </div>
      </div>

      <div className="stats mt-lg">
        <div className="callout">
          <dt>Squad value</dt>
          <dd>
            <strong>{formatPrice(squadValue)}</strong>
          </dd>
        </div>
        <div className="callout">
          <dt>In the bank</dt>
          <dd>
            <strong>{formatPrice(Math.round(bank * 10))}</strong>
          </dd>
        </div>
      </div>

      {POSITIONS.map((position) => (
        <div key={position}>
          <h3>{POSITION_LABELS[position]}s</h3>
          {slots[position].map((player, index) => (
            <div className="row" key={`${position}-${index}`}>
              <PlayerPicker
                value={toPickerValue(player)}
                onChange={(value) => onPickerChange(position, index, value)}
                label={`${POSITION_LABELS[position]} ${index + 1}`}
              />

              {player && (
                <>
                  <button
                    type="button"
                    className="ghost small"
                    aria-pressed={player.isStarting}
                    aria-label={`Toggle starting: ${player.name}`}
                    onClick={() => toggleStarting(position, index)}
                  >
                    {player.isStarting ? 'Starting' : 'Bench'}
                  </button>
                  <button
                    type="button"
                    className="ghost small role-captain"
                    aria-pressed={player.isCaptain}
                    aria-label={`Captain: ${player.name}`}
                    onClick={() => toggleCaptain(position, index)}
                  >
                    C
                  </button>
                  <button
                    type="button"
                    className="ghost small role-vice"
                    aria-pressed={player.isViceCaptain}
                    aria-label={`Vice-captain: ${player.name}`}
                    onClick={() => toggleVice(position, index)}
                  >
                    V
                  </button>
                  {!player.isStarting && (
                    <div className="field">
                      <label htmlFor={`bench-order-${position}-${index}`}>Bench order for {player.name}</label>
                      <input
                        id={`bench-order-${position}-${index}`}
                        type="number"
                        min={1}
                        max={4}
                        value={player.benchOrder ?? ''}
                        onChange={(e) => setBenchOrder(position, index, e.target.value)}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      ))}

      {problems.length > 0 && (
        <ul className="notices">
          {problems.map((problem) => (
            <li key={problem} className="error">
              {problem}
            </li>
          ))}
        </ul>
      )}

      <div className="row">
        <button type="button" onClick={onSaveClick} disabled={saving} aria-busy={saving}>
          {saving && <span className="spinner" aria-hidden="true" />}
          {saving ? 'Saving…' : 'Save squad'}
        </button>
        <button type="button" className="ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </section>
  );
}
